import { useEffect, useState } from "react";
import { Phone, Mail, MapPin, Clock, MapPinned } from "lucide-react";
import { SITE } from "../lib/site";
import { useSiteSettings } from "../lib/settings";
import { useContent } from "../lib/content";
import { ContactForm } from "./ContactForm";
import { Reveal } from "./Reveal";

export const ContactBlock = ({ showTitle = true }) => {
  const { settings } = useSiteSettings();
  const cc = useContent("contact");
  const c = {
    address: settings?.address || SITE.contact.address,
    phone: settings?.phone || SITE.contact.phone,
    email: settings?.email || SITE.contact.email,
    hours: settings?.hours || SITE.contact.hours,
    zone: settings?.zone || SITE.contact.zone,
    mapEmbed: settings?.map_embed || SITE.mapEmbed,
  };
  return (
    <section data-testid="contact-block" className="py-20 md:py-24 bg-white">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {showTitle && (
          <Reveal className="mb-14 text-center">
            <h2 className="font-display text-2xl md:text-3xl font-bold tracking-wide uppercase">
              {cc.form_title || "Nous contacter"}
            </h2>
            <div className="w-16 h-0.5 bg-[#E60000] mx-auto mt-6" />
          </Reveal>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-10">
          {/* Form */}
          <div className="lg:col-span-7">
            <ContactForm compact />
          </div>

          {/* Light-gray contact card + Google Maps */}
          <aside className="lg:col-span-5">
            <div className="bg-[#F4F4F4] border border-gray-200 p-8 lg:p-10">
              <p className="overline mb-2"><span className="red-dot" />Coordonnées</p>
              <p className="font-display text-xl font-bold tracking-tight mb-6">
                {SITE.legalName}
              </p>

              <ul className="space-y-5 text-sm text-gray-700">
                <li className="flex items-start gap-3">
                  <MapPin className="w-4 h-4 text-[#E60000] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-0.5">Adresse</p>
                    <span>{c.address}</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Phone className="w-4 h-4 text-[#E60000] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-0.5">Téléphone</p>
                    <a href={`tel:${c.phone.replace(/\s/g, '')}`} className="hover:text-[#E60000] transition-colors">{c.phone}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Mail className="w-4 h-4 text-[#E60000] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-0.5">E-mail</p>
                    <a href={`mailto:${c.email}`} className="hover:text-[#E60000] transition-colors break-all">{c.email}</a>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <Clock className="w-4 h-4 text-[#E60000] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-0.5">Horaires</p>
                    <span>{c.hours}</span>
                  </div>
                </li>
                <li className="flex items-start gap-3">
                  <MapPinned className="w-4 h-4 text-[#E60000] mt-1 shrink-0" strokeWidth={1.5} />
                  <div>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-gray-500 mb-0.5">Zone d&apos;intervention</p>
                    <span>{c.zone}</span>
                  </div>
                </li>
              </ul>

              <MapEmbed src={c.mapEmbed} />
            </div>
          </aside>
        </div>
      </div>
    </section>
  );
};

const MapEmbed = ({ src }) => {
  const [allowed, setAllowed] = useState(() => {
    try {
      const stored = JSON.parse(localStorage.getItem("ecogest_cookie_consent_v1") || "null");
      return !!(stored && stored.analytics === true);
    } catch (_e) { return false; }
  });
  useEffect(() => {
    const onUpdate = () => {
      try {
        const stored = JSON.parse(localStorage.getItem("ecogest_cookie_consent_v1") || "null");
        setAllowed(!!(stored && stored.analytics === true));
      } catch (_e) { /* ignore */ }
    };
    window.addEventListener("ecogest-consent-updated", onUpdate);
    return () => window.removeEventListener("ecogest-consent-updated", onUpdate);
  }, []);

  if (!allowed) {
    return (
      <div data-testid="map-consent-placeholder" className="mt-8 border border-gray-200 bg-gray-50 p-6 text-center text-sm text-gray-600">
        <MapPinned className="w-6 h-6 text-[#E60000] mx-auto mb-3" strokeWidth={1.5} />
        <p className="font-semibold mb-1">Carte Google Maps désactivée</p>
        <p className="text-xs leading-relaxed mb-4">
          Pour afficher notre localisation, acceptez les cookies tiers dans la bannière en bas de page.
        </p>
        <button
          data-testid="map-enable-consent"
          onClick={() => {
            localStorage.setItem("ecogest_cookie_consent_v1", JSON.stringify({ analytics: true, ts: Date.now() }));
            window.dispatchEvent(new Event("ecogest-consent-updated"));
            window.location.reload();
          }}
          className="inline-flex items-center gap-2 text-xs uppercase tracking-wider bg-[#E60000] hover:bg-[#B30000] text-white px-4 py-2 transition-colors"
        >
          Activer la carte
        </button>
      </div>
    );
  }

  return (
    <div className="mt-8 border border-gray-200 overflow-hidden bg-white">
      <iframe
        title="Localisation Eco-Gest Patrimoine — Google Maps"
        src={src}
        width="100%"
        height="240"
        style={{ border: 0 }}
        allowFullScreen=""
        loading="lazy"
        referrerPolicy="no-referrer-when-downgrade"
        data-testid="contact-google-map"
      />
    </div>
  );
};

export default ContactBlock;
